


import { Component, Vue } from "vue-property-decorator";

import { parsePhoneNumberFromString } from "libphonenumber-js";

import { CustomNotification } from "@Internal/Models";

import { ConsoleUsers, Records } from "App";
import { User, PagerSorter, Column } from "App/Models";
import { Dictionary, Filter, Identifiable } from "App/Contracts";
import { EventBus, $t } from "App/IOC";

import { TableAction } from "Web/Contracts";

import RecordsComponent from "Web/Components/Records.vue";
import InviteForm from "./InviteForm.vue";

@Component({
    components: {
        InviteForm,
        Records: RecordsComponent
    }
})
export default class ConsoleUsersView extends Vue {
    $refs!: Vue[ "$refs" ] & {
        Records: RecordsComponent,
    };

    private columns: Column[] = [];
    private records: User[] = [];

    private id: string = "ConsoleUsers";

    private filters = ConsoleUsers.Filters.Get;

    private pagerSorter: PagerSorter = PagerSorter.ForColumn(
        ConsoleUsers.Columns.GetDefaultSort(), 15, false
    );

    private actions: TableAction<User>[] = [
        {
            Id: "disable",
            ShortName: "block",
            Name: $t( "CONSOLE_USERS_actionDisable" ),
            Multi: false
        },
        {
            Id: "reactivate",
            ShortName: "check_circle",
            Name: $t( "CONSOLE_USERS_actionReactivate" ),
            Multi: false
        }
    ];

    private showForm: boolean = false;

    private async created() {
        const [ columns, error ] = await ConsoleUsers.Columns.Get();

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.columns = columns;
    }

    /* Disable only on a live account, Reactivate only on a disabled one. */
    private isActionAllowed( row: Identifiable, action: Identifiable ): boolean {
        const user = row as User;

        if( action.Id === "disable" )
            return user.State !== "disabled";

        if( action.Id === "reactivate" )
            return user.State === "disabled";

        return true;
    }

    private async update(
        filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ) {
        await this.$nextTick();
        this.$refs.Records.Loading();

        if( resetPagination )
            this.pagerSorter.Page = 1;

        const [ records, updatedPagerSorter, error ] =
            await ConsoleUsers.Find( filters, pagerSorter );

        /* The list rows are plain objects (the Internal/Users payload carries no $Model
           annotation), so the User model's display logic never runs and the "Nom" column
           (DisplayName) is empty. Build it here from FirstName + LastName, falling back to
           Email — same rule as the fiche. */
        this.records = records.map( ( record: User ): User => {
            const name = [ record.FirstName, record.LastName ]
                .filter( ( part?: string ): boolean => part !== undefined && part !== "" )
                .join( " " );

            ( record as unknown as Dictionary ).DisplayName =
                name !== "" ? name : ( record.Email || "" );

            /* Show the phone in national format (06 08 89 79 22) rather than the stored E.164
               (+33608897922) — same widget formatting as the fiche. */
            if( record.Phone !== undefined && record.Phone !== "" ) {
                const parsed = parsePhoneNumberFromString( record.Phone );

                if( parsed !== undefined )
                    record.Phone = parsed.formatNational();
            }

            return record;
        } );

        this.pagerSorter = updatedPagerSorter;

        if( error !== undefined )
            EventBus.Publish( "Error", error );
    }

    private toggleColumn( column: Column ) {
        ConsoleUsers.Columns.Toggle( column );
    }

    private executeAction( id: string, users: User[] ) {
        const urls: string[] = [];
        let index: number = 0;

        switch( id ) {
            case "open":
                this.records.forEach( record => urls.push( "/settings/team/" + record.Id ) );

                index = this.records.findIndex( record => record.Id === users[ 0 ].Id );

                Records.Navigation.SetList( urls, index );

                this.$router.push( `/settings/team/${users[ 0 ].Id}` );

                break;

            case "disable":
                EventBus.Publish( "Confirmation", {
                    Props: {
                        Message: this.$t( "CONSOLE_USERS_disableConfirm" ),
                        Actions: [{
                            Id: "disable",
                            Name: this.$t( "CONSOLE_USERS_actionDisable" ),
                            Active: true
                        }]
                    },
                    Events: { Action: () => this.disable( users[ 0 ] ) }
                });

                break;

            case "reactivate":
                this.reactivate( users[ 0 ] );
                break;

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }

    private async disable( user: User ): Promise<void> {
        const error = await ConsoleUsers.Disable( user.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_disabled" ) );
        this.refresh();
    }

    private async reactivate( user: User ): Promise<void> {
        const error = await ConsoleUsers.Reactivate( user.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_reactivated" ) );
        this.refresh();
    }

    private notify( message: string ): void {
        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "CONSOLE_USERS_title" ), message
        ));
    }

    private refresh(): void {
        this.showForm = false;
        this.update( this.$refs.Records.AppliedFilters, this.pagerSorter, true );
    }
}
